import { observable } from "mobx";

import {
  DefaultFullPPGDataSchemaResponse,
  DefaultPPGDataSchemaResponse,
  PermissionBit,
} from "../../../api";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { Merge } from "../../../utils/types";

export type PPGsItem = Merge<
  DefaultFullPPGDataSchemaResponse | DefaultPPGDataSchemaResponse
> & {
  deviceBattery?: number;
  statusPercentage?: Record<string, unknown>;
};

export class PPGs extends DataModel<PPGsItem> {
  @observable accessor idsFilter: string[] | undefined;
  // The full enriched set. `data` (what DataTable renders) is derived from
  // this by applying idsFilter — v2 reads `data` directly and never calls the
  // model's filtering, so the URL-driven id filter is applied here instead.
  @observable private accessor allData: PPGsItem[] = [];

  constructor(
    data: PPGsItem[],
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super(data, permissions, userUUID);

    // access
    this.access = {
      editRules: {
        medical: AccessRules.visible,
        monitoring: AccessRules.visible,
      },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  updateData(data: PPGsItem[]) {
    this.allData = data.map((item) => {
      const deviceStatus = item.measurement_device_status;
      const deviceBattery = deviceStatus?.battery;
      const statusCounts = !deviceStatus
        ? undefined
        : "pkg_status_stats" in deviceStatus
          ? deviceStatus.pkg_status_stats
          : deviceStatus.state_stats;
      return {
        ...item,
        ["deviceBattery"]: deviceBattery,
        ["statusPercentage"]: countsToPercentages(statusCounts),
      };
    });
    this.applyIdsFilter();
  }

  setIdsFilter(ids: string[] | undefined) {
    this.idsFilter = ids;
    this.applyIdsFilter();
  }

  private applyIdsFilter() {
    const idsFilter = this.idsFilter;
    this.data = idsFilter
      ? this.allData.filter((item) => idsFilter.includes(item.uuid))
      : this.allData;
    super.init();
  }
}

function countsToPercentages(counts?: Record<string, number>) {
  if (counts === null || counts === undefined) {
    return counts;
  }
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return Object.fromEntries(
    Object.entries(counts).map(([k, v]) => [
      k,
      `${Math.round((v / total) * 100)}%`,
    ]),
  );
}
